import dayjs from 'dayjs';
import {
  EntryType,
  MyDayActionType,
  UserProgrammeEnrollmentEventType,
} from '../API';
import { daysSince, numDaysInProgramme } from './programmes';
import { arrayIndexWithinDbWeekStartDate } from './planner';
import { calendarDayFromJSDate, formatDayAndMonthInLocale } from './dates';

export const isContentEntryWatched = (
  contentEntryID,
  userProgrammeEnrollmentID,
  myDayActionRecords,
) => {
  const latestCompletedActionRecord = myDayActionRecords.find(
    (r) =>
      r.userProgrammeEnrollmentID === userProgrammeEnrollmentID &&
      r.programmeEntryType === EntryType.CONTENT_ENTRY &&
      r.objectID === contentEntryID &&
      r.actionType === MyDayActionType.COMPLETED,
  );
  return !!latestCompletedActionRecord;
};

export const isMealCompleted = (
  mealID,
  userProgrammeEnrollmentID,
  myDayActionRecords,
) => {
  const latestCompletedActionRecord = myDayActionRecords.find(
    (r) =>
      r.userProgrammeEnrollmentID === userProgrammeEnrollmentID &&
      r.programmeEntryType === EntryType.MEAL &&
      r.objectID === mealID &&
      r.actionType === MyDayActionType.COMPLETED,
  );
  return !!latestCompletedActionRecord;
};

export const isMealSkipped = (
  mealID,
  userProgrammeEnrollmentID,
  myDayActionRecords,
) => {
  const latestSkipUnskipActionRecord = myDayActionRecords.find(
    (r) =>
      r.userProgrammeEnrollmentID === userProgrammeEnrollmentID &&
      r.programmeEntryType === EntryType.MEAL &&
      r.objectID === mealID &&
      [MyDayActionType.SKIPPED, MyDayActionType.UNSKIPPED].includes(
        r.actionType,
      ),
  );
  if (!latestSkipUnskipActionRecord) {
    return false;
  }
  return latestSkipUnskipActionRecord.actionType === MyDayActionType.SKIPPED;
};

export const isDateWithinEnrollmentPeriod = (
  currentProgrammeEnrollment,
  sharedProgramme,
  todaysDate,
) => {
  if (!currentProgrammeEnrollment || !sharedProgramme) {
    return false;
  }
  const programmeStartDay = programmeStartCalendarDay(
    currentProgrammeEnrollment,
  );
  const daysSinceStart = daysSince(todaysDate, programmeStartDay);
  const programmeDuration = numDaysInProgramme(sharedProgramme);
  return daysSinceStart >= 0 && daysSinceStart < programmeDuration;
};

export const mealSkippedActionRecord = (
  spaceMembershipID,
  userProgrammeEnrollmentID,
  mealID,
  parentID,
  calendarDay,
) => ({
  spaceMembershipID,
  userProgrammeEnrollmentID,
  programmeEntryType: EntryType.MEAL,
  objectID: mealID,
  parentID,
  calendarDay,
  actionType: MyDayActionType.SKIPPED,
});

export const mealUnskippedActionRecord = (
  spaceMembershipID,
  userProgrammeEnrollmentID,
  mealID,
  parentID,
  calendarDay,
) => ({
  spaceMembershipID,
  userProgrammeEnrollmentID,
  programmeEntryType: EntryType.MEAL,
  objectID: mealID,
  parentID,
  calendarDay,
  actionType: MyDayActionType.UNSKIPPED,
});

export const mealCompletedActionRecord = (
  spaceMembershipID,
  userProgrammeEnrollmentID,
  mealID,
  parentID,
  calendarDay,
) => ({
  spaceMembershipID,
  userProgrammeEnrollmentID,
  programmeEntryType: EntryType.MEAL,
  objectID: mealID,
  parentID,
  calendarDay,
  actionType: MyDayActionType.COMPLETED,
});

export const contentEntryWatchedActionRecord = (
  spaceMembershipID,
  userProgrammeEnrollmentID,
  contentEntryID,
  parentID,
  calendarDay,
) => ({
  spaceMembershipID,
  userProgrammeEnrollmentID,
  programmeEntryType: EntryType.CONTENT_ENTRY,
  objectID: contentEntryID,
  parentID,
  calendarDay,
  actionType: MyDayActionType.COMPLETED,
});

export const getContentEntriesForCalendarDay = (
  sharedProgramme,
  programmeStartDay,
  today,
) => {
  const flattenedProgrammeDays = sharedProgramme.plans.flatMap(
    (plan) => plan.days,
  );
  const dayArrayIndex = arrayIndexWithinDbWeekStartDate(
    programmeStartDay,
    today,
  );
  const programmeDay = flattenedProgrammeDays[dayArrayIndex];
  if (!programmeDay) {
    console.warn(
      `Could not find programme day with index ${dayArrayIndex} in programme with ID ${sharedProgramme.id}`,
    );
    return [];
  }
  return programmeDay.entries.filter(
    (entry) => entry.programmeEntryType === EntryType.CONTENT_ENTRY,
  );
};

const friendlyDayNodeLabel = (calendarDay, today) => {
  if (calendarDay.localeCompare(today) === 0) {
    return 'Today';
  }
  return formatDayAndMonthInLocale(calendarDay, 'US');
};

export const programmeStartCalendarDay = (userProgrammeEnrollment) => {
  if (!userProgrammeEnrollment) {
    return null;
  }
  const startEvent = (userProgrammeEnrollment.eventRecords || []).find(
    (event) =>
      event.eventType.includes(UserProgrammeEnrollmentEventType.USER_STARTED),
  );
  if (startEvent) {
    return startEvent.calendarDay;
  }
  const enrollmentEvent = (userProgrammeEnrollment.eventRecords || []).find(
    (event) =>
      event.eventType.includes(UserProgrammeEnrollmentEventType.USER_ENROLLED),
  );
  if (enrollmentEvent) {
    return enrollmentEvent.calendarDay;
  }
  return calendarDayFromJSDate(new Date(userProgrammeEnrollment.enrolledAt));
};

export const programmeTotalDays = (sharedProgramme) =>
  sharedProgramme.plans.flatMap((plan) => plan.days).length;

const myDayActionRecordsFor = (
  myDayActionRecords,
  userProgrammeEnrollmentID,
  calendarDay,
) =>
  myDayActionRecords.filter(
    (r) =>
      r.userProgrammeEnrollmentID === userProgrammeEnrollmentID &&
      r.calendarDay === calendarDay,
  );

export const timelineItemNodesFor = (
  currentProgrammeEnrollment,
  sharedProgramme,
  myDayActionRecords,
  today,
) => {
  if (!currentProgrammeEnrollment || !sharedProgramme) {
    return [];
  }
  const programmeStartDate = programmeStartCalendarDay(
    currentProgrammeEnrollment,
  );
  const programmeLength = sharedProgramme
    ? programmeTotalDays(sharedProgramme)
    : 0;
  const programmeEndsBefore = dayjs(programmeStartDate)
    .add(programmeLength, 'day')
    .format('YYYY-MM-DD');
  const isProgrammeEnded = today.localeCompare(programmeEndsBefore) >= 0;
  const nodes = [];
  if (isProgrammeEnded) {
    nodes.push({
      nodeLabel: 'Congratulations, you have completed the program!',
    });
  }
  let currentDay = today;
  do {
    // console.log({ currentDay });
    const dayItems = [];
    const contentEntries = getContentEntriesForCalendarDay(
      sharedProgramme,
      programmeStartDate,
      currentDay,
    );
    dayItems.push(
      ...contentEntries.map((entry) => ({
        itemType: 'CONTENT_ENTRY',
        itemID: entry.objectID,
        parentID: sharedProgramme.id,
      })),
    );
    const todaysActionRecords = myDayActionRecordsFor(
      myDayActionRecords || [],
      currentProgrammeEnrollment.id,
      currentDay,
    );
    dayItems.push(
      ...todaysActionRecords
        .filter((r) => r.actionType === MyDayActionType.COMPLETED)
        .map((r) => ({
          itemType: 'MEAL',
          itemID: r.objectID,
          parentID: r.parentID,
        })),
    );
    if (dayItems.length > 0) {
      nodes.push({
        nodeLabel: friendlyDayNodeLabel(currentDay, today),
        items: dayItems,
      });
    }
    currentDay = dayjs(currentDay || undefined)
      .add(-1, 'day')
      .format('YYYY-MM-DD');
  } while (currentDay.localeCompare(programmeStartDate) >= 0);
  nodes.push({ nodeLabel: 'You are at the beginning of the program' });
  return nodes;
};

export const recentlyLoggedMealsActionRecords = (myDayActionRecords) => {
  const LOOK_BACK_MAX_WEEKS = 4;
  const minCreatedAt = dayjs().add(-LOOK_BACK_MAX_WEEKS, 'week').toISOString();
  const actionRecords = myDayActionRecords.filter(
    (ar) =>
      ar.actionType === MyDayActionType.COMPLETED &&
      ar.createdAt.localeCompare(minCreatedAt) >= 0,
  );
  return actionRecords;
};

export const isProgrammeEnrollmentCompleted = (
  userProgrammeEnrollment,
  programmeDuration,
  todaysDate,
) => {
  if (!userProgrammeEnrollment) {
    return false;
  }
  const programmeStartDay = programmeStartCalendarDay(userProgrammeEnrollment);
  const daysSinceStart = daysSince(todaysDate, programmeStartDay);
  return daysSinceStart >= programmeDuration;
};
